/**
 * OperatingModePanel — J6 (PRD §12.3.9 frontend page #7 + §1.3 + GOV-03)
 *
 * Replaces the prior read-only display with an interactive CEO-only flow:
 *
 *   1. Display each of the 3 operating modes (founder / growth / scaling) with
 *      a one-line description of what each unlocks.
 *   2. Selecting a different mode opens a wizard:
 *        a. Pre-selected Trunk impact via ImpactClassificationPicker (J2)
 *        b. Embedded J5 DecisionFormDialog so the CEO authors a Decision Note
 *        c. After the Decision Note is created (status=approved automatically
 *           when the user is the CEO actor on Trunk impact), the panel calls
 *           PATCH /api/v1/roros/tenant-settings { operatingMode, decisionNoteId }
 *           — the server enforces GOV-03 by re-reading the Decision Note.
 *   3. CEO-only — gated on `roros.operating-mode.change` permission.
 *   4. Demotion (Growth → Founder) is hidden behind a "Show advanced" toggle.
 *
 * Cadence: backend auto-creates a monthly Mode Check schedule when promoting
 * to growth/scaling. The user does not need to opt in; this panel just kicks
 * the mutation and lets the server do the right thing.
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, ArrowUpRight, ChevronDown, ShieldCheck } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Alert } from '@/components/ui/Alert';
import { Switch } from '@/components/ui/Switch';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { showToast } from '@/components/ui/Toast';
import { QueryPage } from '@/components/QueryPage';
import { t } from '@/lib/i18n';
import { useRorosTenantSettings, useRorosTenantSettingsUpdate } from '@/hooks/roros/launchpad';
import { usePermissions } from '@/hooks/usePermissions';
import { PERMISSIONS } from '@/constants/permissions';
import { DecisionFormDialog } from '@/routes/roros/decisions/DecisionFormDialog';

// ─── Mode definitions ───────────────────────────────────────────────────────

export type OperatingMode = 'founder' | 'growth' | 'scaling';

interface ModeInfo {
  value: OperatingMode;
  label: string;
  oneLiner: string;
  unlocks: string[];
  rank: number;
}

const MODES: ModeInfo[] = [
  {
    value: 'founder',
    label: t('Founder'),
    oneLiner: t('Single owner runs the whole OS — minimal evidence, fast iteration.'),
    unlocks: [
      t('Lite QC allowed'),
      t('Decisions can be informal'),
      t('No mandatory cadence beyond Weekly + Quarterly'),
    ],
    rank: 0,
  },
  {
    value: 'growth',
    label: t('Growth'),
    oneLiner: t('First hires + delegation — Initiatives Portfolio, Cadence Scorecard, full QC.'),
    unlocks: [
      t('Initiatives Portfolio surfaces in nav'),
      t('Cadence Scorecard surfaces in nav'),
      t('Daily Check + Monthly Mode Check cadences activate'),
      t('Full QC evidence required on releases'),
    ],
    rank: 1,
  },
  {
    value: 'scaling',
    label: t('Scaling'),
    oneLiner: t('Multi-team operations — full governance, formal Role Contracts, advisory boards.'),
    unlocks: [
      t('All Growth surfaces stay on'),
      t('Advisory Board consultations expected on Trunk+Root decisions'),
      t('Role Contracts must be signed before any new actor onboards'),
    ],
    rank: 2,
  },
];

function modeInfo(value: string | null): ModeInfo | null {
  if (!value) return null;
  return MODES.find((m) => m.value === value) ?? null;
}

// ─── Subcomponent: Mode card ─────────────────────────────────────────────────

interface ModeCardProps {
  mode: ModeInfo;
  isCurrent: boolean;
  isDisabled: boolean;
  canManage: boolean;
  onSelect: () => void;
}

// TB-4 (AC-SSV-04): canManage controls whether promote buttons are rendered.
// Non-managers see mode info read-only (no promote button); managers see the
// promote button (disabled if demotion lock applies).
function ModeCard({ mode, isCurrent, isDisabled, canManage, onSelect }: ModeCardProps) {
  return (
    <Card
      padding="md"
      data-testid={`operating-mode-card-${mode.value}`}
      data-current={isCurrent ? 'true' : 'false'}
      className={`flex flex-col h-full ${isCurrent ? 'border-2 border-primary-500' : ''}`}
    >
      {/* AC-OpMode-2: pill is placed OUTSIDE CardHeader so it does not trip the
          CardHeader auto-flex heuristic (which fires when any child is not a
          CardTitle or CardDescription). With the Badge as a sibling element in
          the Card's own flex-col flow, pill → headline → sub-headline stack
          vertically as required. */}
      {isCurrent && (
        <Badge
          variant="primary"
          className="mb-2 w-fit"
          data-testid={`operating-mode-pill-${mode.value}`}
        >
          {t('Current')}
        </Badge>
      )}
      <CardHeader>
        <CardTitle data-testid={`operating-mode-headline-${mode.value}`}>
          {mode.label}
        </CardTitle>
        <CardDescription data-testid={`operating-mode-subheadline-${mode.value}`}>
          {mode.oneLiner}
        </CardDescription>
      </CardHeader>
      <ul className="space-y-1 text-xs text-muted-foreground flex-1">
        {mode.unlocks.map((u, i) => (
          <li key={i}>• {u}</li>
        ))}
      </ul>
      {/* TB-4 (AC-SSV-04): Only managers see the promote button. */}
      {!isCurrent && canManage && (
        <Button
          type="button"
          size="sm"
          variant={mode.rank === 0 ? 'outline' : 'primary'}
          disabled={isDisabled}
          onClick={onSelect}
          className="mt-3"
          data-testid={`operating-mode-switch-${mode.value}`}
        >
          <ArrowUpRight className="w-3.5 h-3.5 mr-1.5" aria-hidden />
          {mode.rank === 0 ? t('Switch back to Founder') : t('Promote to {{mode}}').replace('{{mode}}', mode.label)}
        </Button>
      )}
    </Card>
  );
}

// ─── Main panel ──────────────────────────────────────────────────────────────

export function OperatingModePanel() {
  const settingsQuery = useRorosTenantSettings();
  const updateMutation = useRorosTenantSettingsUpdate();
  const { hasPermission, isLoading: permsLoading } = usePermissions();
  const navigate = useNavigate();

  const isCeo = hasPermission(PERMISSIONS.ROROS_OPERATING_MODE_CHANGE);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [pendingMode, setPendingMode] = useState<OperatingMode | null>(null);
  const [showDecisionForm, setShowDecisionForm] = useState(false);
  const [pendingDecisionId, setPendingDecisionId] = useState<string | null>(null);
  const [showApplyConfirm, setShowApplyConfirm] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSelectMode = (mode: OperatingMode, currentMode: string | null) => {
    setError(null);
    if (!isCeo) {
      setError(t('Only the CEO can change the operating mode.'));
      return;
    }
    if (mode === currentMode) return;
    const isDemotion = (modeInfo(mode)?.rank ?? 0) < (modeInfo(currentMode)?.rank ?? 0);
    if (isDemotion && !showAdvanced) {
      setError(t('Demotions require the "Show advanced" toggle to be enabled.'));
      return;
    }
    setPendingMode(mode);
    // Open the J5 Decision Note form. The user authors a Trunk-impact decision
    // documenting the rationale. After approval, we apply the mode change.
    setShowDecisionForm(true);
  };

  const handleDecisionCreated = () => {
    setShowDecisionForm(false);
    // After creating the decision the user is taken to the decision detail
    // to approve it — but for this MVP we resolve the most-recent decision in
    // status=approved with impact=trunk for the current org. Simplest UX:
    // direct the user to the Decision Log to approve, then return.
    showToast.info(t('Decision Note created. Approve it in the Decision Log, then return to apply the mode change.'));
    navigate('/roros/decisions');
  };

  // Manual flow: paste-or-select a Decision Note id. Since GOV-03 enforcement
  // is server-side and the J5 dialog produces a draft, the panel also offers
  // a one-shot "I have an approved Decision Note id" entry path so QA + Dev
  // can exercise the contract without round-tripping to the Decision Log.
  const [decisionIdInput, setDecisionIdInput] = useState('');

  const handleApply = () => {
    if (!pendingMode) return;
    const noteId = pendingDecisionId ?? decisionIdInput.trim();
    if (!noteId) {
      setError(t('A Decision Note id is required to change the operating mode (GOV-03).'));
      return;
    }
    updateMutation.mutate(
      { operatingMode: pendingMode, decisionNoteId: noteId },
      {
        onSuccess: () => {
          showToast.success(t('Operating mode changed'));
          setPendingMode(null);
          setPendingDecisionId(null);
          setDecisionIdInput('');
          setShowApplyConfirm(false);
        },
        onError: (err: any) => {
          setError(err?.message ?? t('Failed to change operating mode'));
          setShowApplyConfirm(false);
        },
      },
    );
  };

  return (
    <QueryPage query={settingsQuery}>
      {(data) => {
        const settings = data as { operating_mode: string | null };
        const currentMode = settings.operating_mode;
        const current = modeInfo(currentMode);
        return (
          <div className="space-y-6">
            {!isCeo && !permsLoading && (
              <Alert variant="info" title={t('Read-only view')} data-testid="operating-mode-readonly-hint">
                {t('Only the CEO can change the operating mode. You can still see what each mode unlocks.')}
              </Alert>
            )}

            {error && (
              <Alert variant="error" title={t('Error')}>
                <div className="flex items-start gap-2">
                  <AlertCircle className="h-4 w-4 mt-0.5" aria-hidden />
                  <span>{error}</span>
                </div>
              </Alert>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {MODES.map((mode) => {
                const isCurrent = mode.value === currentMode;
                const isDemotion = (mode.rank) < (current?.rank ?? 0);
                // Demotion mode-cards are disabled unless the "Advanced" toggle is on; non-demotion cards are always enabled.
                // Note: the promote/select button only renders at all for manager-role users (canManage=isCeo guard in ModeCard).
                const isDisabled = isDemotion && !showAdvanced;
                return (
                  <ModeCard
                    key={mode.value}
                    mode={mode}
                    isCurrent={isCurrent}
                    isDisabled={isDisabled}
                    canManage={isCeo}
                    onSelect={() => handleSelectMode(mode.value, currentMode)}
                  />
                );
              })}
            </div>

            {isCeo && (
              <Card padding="sm" className="flex items-center justify-between" data-testid="operating-mode-advanced-toggle">
                <div>
                  <p className="text-sm font-medium text-foreground flex items-center gap-1.5">
                    <ChevronDown className="w-4 h-4" aria-hidden />
                    {t('Show advanced')}
                  </p>
                  <p className="text-xs text-muted-foreground" data-testid="operating-mode-advanced-hint">
                    {t('Demotion paths coming in a later release.')}
                  </p>
                </div>
                <Switch
                  checked={showAdvanced}
                  disabled
                  onChange={undefined}
                  aria-label={t('Show advanced demotion options')}
                />
              </Card>
            )}

            {/* GOV-03 server-side enforcement explainer */}
            <Card padding="md" className="bg-info-50/40 border-info-200" data-testid="operating-mode-gov03-note">
              <div className="flex items-start gap-3">
                <ShieldCheck className="w-5 h-5 text-info-600 mt-0.5" aria-hidden />
                <div className="space-y-1">
                  <p className="text-sm font-medium text-foreground">{t('Mode changes require an approved Decision Note.')}</p>
                  <p className="text-xs text-muted-foreground">
                    {t('When you switch modes, ROROS opens a Decision Note pre-set to Trunk impact. After the note is approved, paste its id below and apply the change. The server re-checks the note before persisting.')}
                  </p>
                </div>
              </div>
            </Card>

            {/* Apply panel — visible after a mode is picked */}
            {pendingMode && isCeo && (
              <Card padding="md" data-testid="operating-mode-apply-panel">
                <CardHeader>
                  <CardTitle>{t('Apply mode change')}</CardTitle>
                  <CardDescription>
                    {t('Pending: {{mode}} (Trunk-impact change)').replace('{{mode}}', modeInfo(pendingMode)?.label ?? pendingMode)}
                  </CardDescription>
                </CardHeader>
                <div className="space-y-3">
                  <label className="block text-sm font-medium text-foreground">
                    {t('Approved Decision Note id')} <span className="text-error-500" aria-hidden>*</span>
                  </label>
                  <Input
                    value={decisionIdInput}
                    onChange={(e) => setDecisionIdInput(e.target.value)}
                    placeholder={t('UUID of the approved Decision Note')}
                    data-testid="operating-mode-decision-id"
                  />
                  <div className="flex justify-end gap-2">
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => {
                        setPendingMode(null);
                        setDecisionIdInput('');
                        setError(null);
                      }}
                    >
                      {t('Cancel')}
                    </Button>
                    <Button
                      type="button"
                      isLoading={updateMutation.isPending}
                      disabled={!decisionIdInput.trim() || updateMutation.isPending}
                      onClick={() => setShowApplyConfirm(true)}
                      data-testid="operating-mode-apply-submit"
                    >
                      {t('Apply mode change')}
                    </Button>
                  </div>
                </div>
              </Card>
            )}

            {/* Decision Note form (J5) — pre-selects Trunk impact in the UI */}
            <DecisionFormDialog
              isOpen={showDecisionForm}
              onClose={() => setShowDecisionForm(false)}
              onCreated={handleDecisionCreated}
              defaultImpact="trunk"
            />

            <ConfirmDialog
              isOpen={showApplyConfirm}
              onClose={() => setShowApplyConfirm(false)}
              onConfirm={handleApply}
              title={t('Confirm operating mode change')}
              description={
                pendingMode
                  ? `${t('You are about to change the operating mode to')} "${modeInfo(pendingMode)?.label}". ${t('This is a Trunk-impact change governed by an approved Decision Note.')}`
                  : ''
              }
              confirmText={t('Apply')}
              cancelText={t('Cancel')}
              variant="primary"
              isLoading={updateMutation.isPending}
            />
          </div>
        );
      }}
    </QueryPage>
  );
}
